import api from '@js/actions/api';

import {mapStore} from '@js/stores/mapStore';

import Computation from '@js/modules/computation';

import {
    RideTypeToRouteType,
    DurationEstimationModes,
    DefaultDurationEstimationMode
} from '@js/components/modules/constants';

// persist: save the choice as the user's default activity (window.currentUserRideType across loads).
// Defaults to true for user-driven changes; pass {persist: false} for programmatic/init calls (e.g. the
// planner seeding its type from a prop) so they don't overwrite the stored default with a non-choice.
export const applyRideType = (rideType, {persist = true} = {}) => {
    mapStore.currentRideType = rideType;
    mapStore.currentRouteType = RideTypeToRouteType[rideType];

    mapStore.map?.setRouteType(mapStore.currentRouteType);

    // Previously persisted server-side as a side effect of the estimate-duration call, which no longer
    // runs client-side.
    if (persist) {
        saveUserSettings({default_ride_type: rideType});
    }
};

export const computeRideElevation = (elevationData, requestOptions = {}) => (
    api.post('/api/v1/rides/compute-elevation', {
        elevation: elevationData
    }, false, requestOptions)
);

export const computeRideDuration = (durationData, requestOptions = {}) => (
    api.post('/api/v1/rides/estimate-duration', {
        elevation: {
            paceProfile: mapStore.currentPaceProfile,
            ...durationData
        }
    }, false, requestOptions)
);

// Persist a partial patch of the connected user's settings (default_ride_type, pace_profile…). Merged
// server-side (users#update_settings), so it never wipes the other stored keys. Fire-and-forget: a failed
// save must never block or break the UI, and is a no-op for anonymous visitors.
export const saveUserSettings = (settings) => {
    if (!window.currentUserId || !settings || !Object.keys(settings).length) {
        return Promise.resolve();
    }

    return api.update(`/api/v1/users/${window.currentUserId}/settings`, {settings}).catch(() => null);
};

export const saveUserPaceProfile = (paceProfile) => (
    paceProfile ? saveUserSettings({pace_profile: paceProfile}) : Promise.resolve()
);

export const estimateRideDurationLocally = ({
                                                rideType,
                                                distance,
                                                paceProfile
                                            } = {}) => {
    if (!distance) {
        return Promise.resolve(null);
    }

    const build = (coordinateElevations) => {
        if (!coordinateElevations?.length) {
            return null;
        }

        const elevationByDistance = coordinateElevations.map((coordEl) => [coordEl.deltaDistance, coordEl.elevation]);
        const duration = Computation.estimateServerDuration(rideType, distance, elevationByDistance, {
            paceProfile: paceProfile || mapStore.currentPaceProfile
        });

        return {
            duration,
            chronoDuration: Computation.formatDuration(duration, {chronoFormat: true})
        };
    };

    const cached = mapStore.coordinateElevations;
    if (cached?.length) {
        return Promise.resolve(build(cached));
    }

    const map = mapStore.map;
    if (!map || typeof map.computeElevation !== 'function') {
        return Promise.resolve(null);
    }

    return new Promise((resolve) => {
        let settled = false;
        const done = (result) => {
            if (settled) {
                return;
            }
            settled = true;
            resolve(result);
        };

        const timer = setTimeout(() => done(null), 4000);

        map.computeElevation((coordinateElevations) => {
            clearTimeout(timer);
            done(build(coordinateElevations));
        });
    });
};

export const getDurationEstimationMode = () => (
    window.durationEstimationMode === DurationEstimationModes.SERVER
        ? DurationEstimationModes.SERVER
        : DefaultDurationEstimationMode
);

export const estimateRideDuration = ({
                                         rideType,
                                         distance,
                                         paceProfile
                                     } = {}) => {
    if (getDurationEstimationMode() === DurationEstimationModes.SERVER) {
        return computeRideDuration({
            mapPoints: mapStore.map?.serializePoints?.(),
            distance,
            rideType
        });
    }

    return estimateRideDurationLocally({
        rideType,
        distance,
        paceProfile
    });
};
